import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, CalendarCheck } from "lucide-react";
import { SCHEDULER_URL } from "../lib/constants";

const PACKAGES = [
  {
    slug: "drumtalk",
    name: "DrumTalk",
    who: "Corporate & Large Groups",
    hue: "#548a88",
    price: "from $500",
    priceNote: "date-hold deposit · by scope",
    model: "One-time · applied to final invoice",
    features: ["Keynote openers, breakouts & half-day workshops", "20–500+ participants", "Reserve your date instantly online", "Tailored quote for procurement"],
    cta: { label: "Reserve your date", to: "/drumtalk#drumtalk-reserve", internal: true },
    featured: true,
  },
  {
    slug: "percussion-pathways",
    name: "Percussion Pathways",
    who: "Senior Living & Memory Care",
    hue: "#b89556",
    price: "Custom",
    priceNote: "recurring program",
    model: "Ongoing · tailored to your care level",
    features: ["Independent, Assisted & Memory Care formats", "Weekly, bi-weekly or monthly", "Instrument adaptations included", "Printable info kit for boards"],
    cta: { label: "Book a Cadence Call", to: SCHEDULER_URL, internal: false },
  },
  {
    slug: "percussion-pals",
    name: "Percussion Pals",
    who: "Early Childhood Education",
    hue: "#c46a58",
    price: "Custom",
    priceNote: "recurring program",
    model: "Ongoing · priced per classroom & frequency",
    features: ["Playful rhythm games for ages 3–5", "Rotating classroom sessions", "Free ECE activity guide", "Sensory & motor-skill focus"],
    cta: { label: "Book a Cadence Call", to: SCHEDULER_URL, internal: false },
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-white/5 overflow-hidden" data-testid="pricing-section">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(var(--glow),0.12), transparent 65%)" }} />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-primary-c/70" />
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c">Packages &amp; Pricing</p>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.04]">A gentle first step toward relief.</h2>
        <p className="mt-4 text-fg/60 md:text-lg max-w-2xl">Reserve a keynote date online, or start a warm, no-pressure conversation about an ongoing program tuned to your community&rsquo;s needs.</p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`pricing-card-${p.slug}`}
              className="glass shape-r p-8 flex flex-col relative overflow-hidden"
              style={{ border: `1px solid ${p.hue}${p.featured ? "88" : "33"}` }}
            >
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: p.hue }} />
              {p.featured && (
                <span className="absolute top-5 right-5 font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full" style={{ backgroundColor: `${p.hue}22`, color: p.hue }}>Book online</span>
              )}
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/45">{p.who}</p>
              <h3 className="font-serif text-3xl mt-2" style={{ color: p.hue }}>{p.name}</h3>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-serif text-4xl md:text-5xl">{p.price}</span>
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-fg/50">{p.priceNote}</span>
              </div>
              <p className="mt-1 font-mono text-[10px] tracking-[0.1em] uppercase text-fg/40">{p.model}</p>

              <ul className="mt-7 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-fg/75 text-[15px] leading-snug">
                    <span className="mt-0.5 shrink-0" style={{ color: p.hue }}><Check size={16} /></span>
                    {f}
                  </li>
                ))}
              </ul>

              {p.cta.internal ? (
                <Link
                  to={p.cta.to}
                  data-testid={`pricing-cta-${p.slug}`}
                  className="mt-8 inline-flex items-center justify-center gap-2 shape-r font-medium px-6 py-3.5 transition-opacity hover:opacity-90"
                  style={{ backgroundColor: p.hue, color: "#141b1d" }}
                >
                  <CalendarCheck size={16} /> {p.cta.label}
                </Link>
              ) : (
                <a
                  href={p.cta.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pricing-cta-${p.slug}`}
                  className="mt-8 inline-flex items-center justify-center gap-2 shape-r font-medium px-6 py-3.5 transition-colors hover:bg-white/10"
                  style={{ border: `1px solid ${p.hue}`, color: p.hue }}
                >
                  {p.cta.label} <ArrowRight size={16} />
                </a>
              )}
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center font-mono text-[11px] tracking-[0.15em] uppercase text-fg/40">
          Also serving youth &amp; schools through{" "}
          <Link to="/program/trap-learning" className="text-primary-c hover:underline">TRAP Learning →</Link>
        </p>
      </div>
    </section>
  );
}
